/**
 * UI Store
 *
 * 전역 UI 상태 관리 (사이드바, 정렬, 뷰 모드, 모달 등)
 * 기존 sidebarStore를 확장합니다.
 */

import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';

// ============================================================================
// Types
// ============================================================================

export type SortOption = 'created_at' | 'updated_at' | 'name' | 'status';
export type ViewMode = 'grid' | 'list';

interface UIState {
  // 사이드바
  sidebarExpanded: boolean;
  mobileMenuOpen: boolean;

  // 플레이리스트 목록 설정
  playlistSort: SortOption;
  playlistViewMode: ViewMode;
  selectedChannelId: string | null;

  // 모달 상태
  activeModal: string | null;
  modalData: Record<string, unknown> | null;

  // 크레딧 부족 모달
  insufficientCredits: { required: number; balance: number } | null;
}

interface UIActions {
  // 사이드바
  toggleSidebar: () => void;
  setSidebarExpanded: (expanded: boolean) => void;
  setMobileMenuOpen: (open: boolean) => void;
  toggleMobileMenu: () => void;

  // 플레이리스트 설정
  setPlaylistSort: (sort: SortOption) => void;
  setPlaylistViewMode: (mode: ViewMode) => void;
  setSelectedChannelId: (channelId: string | null) => void;

  // 모달
  openModal: (name: string, data?: Record<string, unknown>) => void;
  closeModal: () => void;
  isModalOpen: (name: string) => boolean;

  // 크레딧 부족 모달
  showInsufficientCredits: (required: number, balance: number) => void;
  hideInsufficientCredits: () => void;
}

type UIStore = UIState & UIActions;

// ============================================================================
// Initial State
// ============================================================================

const initialState: UIState = {
  sidebarExpanded: true,
  mobileMenuOpen: false,
  playlistSort: 'created_at',
  playlistViewMode: 'list',
  selectedChannelId: null,
  activeModal: null,
  modalData: null,
  insufficientCredits: null,
};

// ============================================================================
// Storage — 탭별/전역 분리
// ============================================================================

// 탭별로 격리할 키(sessionStorage). 나머지 전역 UI 취향은 localStorage(공유) 유지.
// selectedChannelId를 여기 두면: 여러 탭에서 서로 다른 채널을 독립 유지(교차 오염 방지) +
// 새로고침엔 보존(sessionStorage는 탭 생존 동안 유지). 사이드바·정렬 등은 전 탭 공유 유지.
const PER_TAB_KEYS: readonly string[] = ['selectedChannelId'];

const splitTabStorage: StateStorage = {
  getItem: (name) => {
    if (typeof window === 'undefined') {
      return null;
    }
    const localRaw = window.localStorage.getItem(name);
    const sessionRaw = window.sessionStorage.getItem(name);
    if (!localRaw && !sessionRaw) {
      return null;
    }
    const local = localRaw ? JSON.parse(localRaw) : {};
    const session = sessionRaw ? JSON.parse(sessionRaw) : {};
    // session(탭별)이 local(공유)을 덮어써 탭 고유 선택을 우선.
    return JSON.stringify({
      ...local,
      state: { ...(local.state ?? {}), ...(session.state ?? {}) },
    });
  },
  setItem: (name, value) => {
    if (typeof window === 'undefined') {
      return;
    }
    const parsed = JSON.parse(value);
    const state = (parsed.state ?? {}) as Record<string, unknown>;
    const sessionState: Record<string, unknown> = {};
    const localState: Record<string, unknown> = {};
    for (const key of Object.keys(state)) {
      if (PER_TAB_KEYS.includes(key)) {
        sessionState[key] = state[key];
      } else {
        localState[key] = state[key];
      }
    }
    window.localStorage.setItem(name, JSON.stringify({ ...parsed, state: localState }));
    window.sessionStorage.setItem(name, JSON.stringify({ ...parsed, state: sessionState }));
  },
  removeItem: (name) => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.removeItem(name);
    window.sessionStorage.removeItem(name);
  },
};

// ============================================================================
// Store
// ============================================================================

export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // 사이드바
      toggleSidebar: () => set((state) => ({ sidebarExpanded: !state.sidebarExpanded })),
      setSidebarExpanded: (expanded) => set({ sidebarExpanded: expanded }),
      setMobileMenuOpen: (open) => set({ mobileMenuOpen: open }),
      toggleMobileMenu: () => set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen })),

      // 플레이리스트 설정
      setPlaylistSort: (sort) => set({ playlistSort: sort }),
      setPlaylistViewMode: (mode) => set({ playlistViewMode: mode }),
      setSelectedChannelId: (channelId) => set({ selectedChannelId: channelId }),

      // 모달
      openModal: (name, data = {}) => set({ activeModal: name, modalData: data }),
      closeModal: () => set({ activeModal: null, modalData: null }),
      isModalOpen: (name) => get().activeModal === name,

      // 크레딧 부족 모달
      showInsufficientCredits: (required, balance) =>
        set({ insufficientCredits: { required, balance } }),
      hideInsufficientCredits: () => set({ insufficientCredits: null }),
    }),
    {
      name: 'ui-storage',
      // selectedChannelId는 탭별(sessionStorage), 나머지는 전역(localStorage)로 분리.
      storage: createJSONStorage(() => splitTabStorage),
      partialize: (state) => ({
        sidebarExpanded: state.sidebarExpanded,
        playlistSort: state.playlistSort,
        playlistViewMode: state.playlistViewMode,
        selectedChannelId: state.selectedChannelId,
      }),
    }
  )
);

// ============================================================================
// Hook (기존 useSidebarStore 호환)
// ============================================================================

/**
 * useSidebarStore 훅
 *
 * 기존 sidebarStore와 동일한 API를 제공합니다.
 * 새 코드에서는 useUIStore를 직접 사용하는 것을 권장합니다.
 *
 * @deprecated useUIStore를 사용하세요
 */
export function useSidebarStore() {
  const store = useUIStore();

  return {
    isExpanded: store.sidebarExpanded,
    isMobileMoreOpen: store.mobileMenuOpen,
    toggleExpanded: store.toggleSidebar,
    setExpanded: store.setSidebarExpanded,
    setMobileMoreOpen: store.setMobileMenuOpen,
    toggleMobileMore: store.toggleMobileMenu,
  };
}

// ============================================================================
// Selectors
// ============================================================================

export const selectSidebarExpanded = (state: UIStore) => state.sidebarExpanded;
export const selectMobileMenuOpen = (state: UIStore) => state.mobileMenuOpen;
export const selectPlaylistSort = (state: UIStore) => state.playlistSort;
export const selectPlaylistViewMode = (state: UIStore) => state.playlistViewMode;
export const selectActiveModal = (state: UIStore) => state.activeModal;
export const selectModalData = (state: UIStore) => state.modalData;

export const selectSidebar = (state: UIStore) => ({
  expanded: state.sidebarExpanded,
  mobileOpen: state.mobileMenuOpen,
});

export const selectPlaylistSettings = (state: UIStore) => ({
  sort: state.playlistSort,
  viewMode: state.playlistViewMode,
});

export const selectModal = (state: UIStore) => ({
  active: state.activeModal,
  data: state.modalData,
});
